import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import api from '../api/client';
import { toMiamiDateString } from '../utils/dateUtils';
import { renderTemplate, renderEmployeeMessage } from '../utils/messageTemplate';
import './AssignmentsModal.css';

// Assignments popup (Production Plan): who works where on which product — the
// manager's "Master Bags Production Plan" sheet moved into the app.
// Tabs: Day (assign employees/stations to the day's plan), Messages (texts for
// employees from templates), History (past assignments), Settings (stations,
// shifts, activities, templates). Server: routes/production-assignments.js.

const TYPE_LABELS = { sets: 'Sets', wildmicr: 'Wildflowers & Microgreens', fert_products: 'Fertilizers', kit: 'Kits' };
const PRODUCT_TYPES = Object.keys(TYPE_LABELS);
const PLACEHOLDERS = ['{Employee}', '{Date}', '{Station}', '{Brand}', '{ProductNo}', '{Product}', '{Grams}', '{BoxSize}', '{BagsPerBox}', '{Bags}', '{Boxes}', '{Comment}'];
const errMsg = (e, fallback) => (e.response && e.response.data && e.response.data.error) || fallback;
const fmtUS = iso => (iso ? `${iso.slice(5, 7)}/${iso.slice(8, 10)}/${iso.slice(0, 4)}` : '');
const addDays = (iso, n) => { const d = new Date(iso + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const fmtG = g => (g == null ? '' : `${Number(g).toFixed(2).replace(/\.?0+$/, '')}G`);
const boxesOf = (bags, bpb) => {
  if (bags === '' || bags == null || !bpb) return '';
  const b = Number(bags) / Number(bpb);
  return Number.isInteger(b) ? String(b) : b.toFixed(2);
};
const fmtN = n => Number(n || 0).toLocaleString('en-US');
let keySeq = 1;
const newRow = (init = {}) => ({ key: keySeq++, employee_id: '', target: '', mbsku: '', qty_bags: '', comment: '', ...init });
// saved row (server) → editor row. target encodes the mixed Station/Activity select: 'st:<id>' | 'ac:<name>'
const fromSaved = r => newRow({
  id: r.id, // keeps the server row on save (Packers Report ticks live on it); copied rows get no id
  employee_id: r.employee_id ? String(r.employee_id) : '',
  target: r.station_id ? `st:${r.station_id}` : r.activity ? `ac:${r.activity}` : '',
  mbsku: r.mbsku || '', qty_bags: r.qty_bags == null ? '' : String(r.qty_bags), comment: r.comment || '',
});
const toPayload = r => ({
  id: r.id || null,
  employee_id: Number(r.employee_id),
  station_id: r.target.startsWith('st:') ? Number(r.target.slice(3)) : null,
  activity: r.target.startsWith('ac:') ? r.target.slice(3) : null,
  mbsku: r.target.startsWith('ac:') ? null : (r.mbsku || null),
  qty_bags: r.qty_bags === '' ? null : Number(r.qty_bags),
  comment: r.comment.trim() || null,
});

const copyText = async text => {
  try { await navigator.clipboard.writeText(text); return true; }
  catch {
    const ta = document.createElement('textarea'); // Safari / http fallback
    ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    try { return document.execCommand('copy'); } catch { return false; } finally { document.body.removeChild(ta); }
  }
};

const AssignmentsModal = ({ onClose }) => {
  const [tab, setTab] = useState('day');
  const [date, setDate] = useState(() => toMiamiDateString(new Date()));
  const [shiftId, setShiftId] = useState('');
  const [ctx, setCtx] = useState(null);
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [flash, setFlash] = useState(null);
  const downOnOverlay = useRef(false);

  const say = useCallback(msg => { setFlash(msg); setTimeout(() => setFlash(f => (f === msg ? null : f)), 2500); }, []);

  const loadCtx = useCallback(async d => {
    const r = await api.get('/plantoproduce/assignments/context', { params: { date: d } });
    setCtx(r.data);
    setShiftId(s => s || String(((r.data.shifts || []).find(x => x.is_active) || r.data.shifts[0] || {}).id || ''));
    return r.data;
  }, []);
  const loadRows = useCallback(async (d, sh) => {
    if (!sh) return;
    const r = await api.get('/plantoproduce/assignments', { params: { from: d, to: d, shift_id: sh } });
    setRows((r.data.rows || []).map(fromSaved));
    setDirty(false);
  }, []);

  useEffect(() => { loadCtx(date).catch(e => setErr(errMsg(e, 'Failed to load'))); }, [date, loadCtx]);
  useEffect(() => { loadRows(date, shiftId).catch(e => setErr(errMsg(e, 'Failed to load'))); }, [date, shiftId, loadRows]);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape' && !busy && (!dirty || window.confirm('Discard unsaved changes?'))) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, busy, dirty]);

  const guard = () => !dirty || window.confirm('Discard unsaved changes?');
  const changeDate = d => { if (d && guard()) setDate(d); };
  const changeShift = s => { if (guard()) setShiftId(s); };

  const productsBySku = useMemo(() => new Map(((ctx && ctx.products) || []).map(p => [p.sku, p])), [ctx]);
  const activitiesByName = useMemo(() => new Map(((ctx && ctx.activities) || []).map(a => [a.name, a])), [ctx]);
  const employeesById = useMemo(() => new Map(((ctx && ctx.employees) || []).map(e => [String(e.id), e])), [ctx]);
  const stationsById = useMemo(() => new Map(((ctx && ctx.stations) || []).map(s => [String(s.id), s])), [ctx]);
  const assignedBySku = useMemo(() => {
    const m = new Map();
    for (const r of rows) if (r.mbsku && !r.target.startsWith('ac:')) m.set(r.mbsku, (m.get(r.mbsku) || 0) + (Number(r.qty_bags) || 0));
    return m;
  }, [rows]);
  const planRows = useMemo(() => ((ctx && ctx.products) || []).filter(p => p.plan_qty > 0), [ctx]);

  const patchRow = (key, patch) => { setRows(rs => rs.map(r => (r.key === key ? { ...r, ...patch } : r))); setDirty(true); };
  const removeRow = key => { setRows(rs => rs.filter(r => r.key !== key)); setDirty(true); };
  const addRow = init => { setRows(rs => [...rs, newRow(init)]); setDirty(true); };
  const remainingFor = (sku, exceptKey) => {
    const p = productsBySku.get(sku);
    if (!p) return '';
    const used = rows.filter(r => r.mbsku === sku && r.key !== exceptKey && !r.target.startsWith('ac:')).reduce((s, r) => s + (Number(r.qty_bags) || 0), 0);
    return String(Math.max(p.plan_qty - used, 0));
  };

  const fillFromPlan = () => {
    const add = planRows.filter(p => p.plan_qty - (assignedBySku.get(p.sku) || 0) > 0)
      .map(p => newRow({ mbsku: p.sku, qty_bags: String(p.plan_qty - (assignedBySku.get(p.sku) || 0)) }));
    if (!add.length) return say('Every product of the plan is already assigned');
    setRows(rs => [...rs, ...add]); setDirty(true);
  };
  const copyPrevious = async () => {
    if (rows.length && !window.confirm('Replace the current rows with the previous day\'s layout?')) return;
    setBusy(true); setErr(null);
    try {
      const r = await api.get('/plantoproduce/assignments', { params: { from: addDays(date, -14), to: addDays(date, -1), shift_id: shiftId } });
      const all = r.data.rows || [];
      if (!all.length) { say('No assignments in the previous 14 days'); return; }
      const last = all[0].date; // sorted date DESC
      setRows(all.filter(x => x.date === last).map(x => fromSaved({ ...x, id: undefined }))); setDirty(true);
      say(`Copied ${fmtUS(last)}`);
    } catch (e) { setErr(errMsg(e, 'Failed to copy')); } finally { setBusy(false); }
  };
  const save = async () => {
    const bad = rows.findIndex(r => !r.employee_id);
    if (bad >= 0) return setErr(`Row ${bad + 1}: choose an employee`);
    setBusy(true); setErr(null);
    try {
      await api.put('/plantoproduce/assignments', { date, shift_id: Number(shiftId), rows: rows.map(toPayload) });
      await loadRows(date, shiftId);
      say('Saved');
    } catch (e) { setErr(errMsg(e, 'Failed to save')); } finally { setBusy(false); }
  };

  // ── Messages: one per employee — all their products of the day in a single text ──
  const messages = useMemo(() => {
    if (!ctx) return [];
    const out = [], byEmp = new Map();
    for (const r of rows) {
      const emp = employeesById.get(r.employee_id);
      if (!emp) continue;
      if (r.target.startsWith('ac:')) {
        const act = activitiesByName.get(r.target.slice(3));
        if (act && act.message_template) out.push({ key: r.key, who: emp.name, sub: act.name, text: renderTemplate(act.message_template, { Employee: emp.first_name, Date: fmtUS(date) }) });
        continue;
      }
      if (!r.mbsku) continue;
      const p = productsBySku.get(r.mbsku);
      const st = r.target.startsWith('st:') ? stationsById.get(r.target.slice(3)) : null;
      let g = byEmp.get(r.employee_id);
      if (!g) { byEmp.set(r.employee_id, g = { key: r.key, who: emp.name, parts: [], stations: [], products: [] }); out.push(g); }
      g.parts.push({ template: (ctx.templates || {})[p ? p.type : 'sets'] || '', vars: {
        Employee: emp.first_name, Date: fmtUS(date), Station: st ? st.label : '', Brand: p ? p.brand : '',
        ProductNo: p && p.product_no != null ? p.product_no : '', Product: p ? p.name : r.mbsku,
        Grams: p ? fmtG(p.grams) : '', BoxSize: (p && p.box_size) || '', BagsPerBox: p ? p.bags_per_box : '', Bags: r.qty_bags,
        Boxes: boxesOf(r.qty_bags, p && p.bags_per_box), Comment: r.comment.trim(),
      } });
      if (st && !g.stations.includes(st.label)) g.stations.push(st.label);
      g.products.push(p ? p.name : r.mbsku);
    }
    for (const g of byEmp.values()) {
      g.sub = [g.stations.join(', '), g.products.join(', ')].filter(Boolean).join(' · ');
      g.text = renderEmployeeMessage(g.parts);
    }
    return out;
  }, [ctx, rows, date, employeesById, activitiesByName, productsBySku, stationsById]);

  const shifts = (ctx && ctx.shifts) || [];
  const title = { day: 'Day', messages: 'Messages', history: 'History', settings: 'Settings' };

  return (
    <div className="pa-overlay"
         onMouseDown={e => { downOnOverlay.current = e.target === e.currentTarget; }}
         onClick={e => { if (downOnOverlay.current && e.target === e.currentTarget && !busy && guard()) onClose(); }}>
      <div className="pa-modal">
        <div className="pa-head">
          <div className="pa-title">Assignments</div>
          <div className="pa-tabs">
            {Object.entries(title).map(([k, l]) => (
              <button key={k} className={`pa-tab${tab === k ? ' on' : ''}`} onClick={() => setTab(k)}>{l}{k === 'day' && dirty ? ' •' : ''}</button>
            ))}
          </div>
          {flash && <span className="pa-flash">{flash}</span>}
          <button className="pa-close" onClick={() => { if (!busy && guard()) onClose(); }} title="Close">×</button>
        </div>
        {err && <div className="pp-modal-err pa-err">{err} <button className="pa-link" onClick={() => setErr(null)}>×</button></div>}
        <div className="pa-body">
          {!ctx && <div className="pp-muted">Loading…</div>}
          {ctx && (tab === 'day' || tab === 'messages') && (
            <div className="pa-bar">
              <label>Date <input type="date" value={date} onChange={e => changeDate(e.target.value)} /></label>
              <label>Shift <select value={shiftId} onChange={e => changeShift(e.target.value)}>
                {shifts.filter(s => s.is_active || String(s.id) === shiftId).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select></label>
              <span className="pa-grow" />
              {tab === 'day' && <>
                <button className="pp-btn" onClick={fillFromPlan} disabled={busy} title="One row per product of the day's plan that is not fully assigned yet">Fill from plan</button>
                <button className="pp-btn" onClick={copyPrevious} disabled={busy} title="Copy employees, stations and products from the last day that has assignments">Copy previous day</button>
                <button className="pp-btn" onClick={() => addRow()} disabled={busy}>+ Row</button>
                <button className="pp-btn pp-btn-primary" onClick={save} disabled={busy || !dirty}>{busy ? 'Saving…' : 'Save'}</button>
              </>}
            </div>
          )}
          {ctx && tab === 'day' && (
            <DayTab ctx={ctx} rows={rows} planRows={planRows} assignedBySku={assignedBySku} productsBySku={productsBySku}
                    employeesById={employeesById} patchRow={patchRow} removeRow={removeRow} addRow={addRow} remainingFor={remainingFor} />
          )}
          {ctx && tab === 'messages' && (
            <MessagesTab messages={messages} dirty={dirty} templates={ctx.templates} onTemplates={t => setCtx(c => ({ ...c, templates: t }))} say={say} setErr={setErr} />
          )}
          {ctx && tab === 'history' && <HistoryTab ctx={ctx} date={date} setErr={setErr} />}
          {ctx && tab === 'settings' && <SettingsTab onChanged={() => loadCtx(date).catch(() => {})} setErr={setErr} say={say} />}
        </div>
      </div>
    </div>
  );
};

// ── Day tab ──────────────────────────────────────────────────────────────────
const DayTab = ({ ctx, rows, planRows, assignedBySku, productsBySku, employeesById, patchRow, removeRow, addRow, remainingFor }) => {
  const others = ((ctx.products || []).filter(p => !(p.plan_qty > 0)));
  // station -> [{ rowKey, name }] of the rows already on it (busy marker in the select, excluding the row itself)
  const usersOfStation = useMemo(() => {
    const m = new Map();
    for (const r of rows) if (r.target.startsWith('st:')) {
      const e = employeesById.get(r.employee_id);
      m.set(r.target, [...(m.get(r.target) || []), { rowKey: r.key, name: e ? e.first_name : '?' }]);
    }
    return m;
  }, [rows, employeesById]);

  return (
    <div className="pa-day">
      <aside className="pa-plan">
        <div className="pa-sub">Plan for {fmtUS(ctx.date)} <span className="pp-muted">— click a product to add a row</span></div>
        {!planRows.length && <div className="pp-muted pa-pad">No production planned for this day</div>}
        {planRows.length > 0 && (
          <table className="pa-table pa-plan-table">
            <thead><tr><th>NO.</th><th>Product</th><th className="pa-num">Plan</th><th className="pa-num">Assigned</th><th className="pa-num">Left</th></tr></thead>
            <tbody>
              {planRows.map(p => {
                const a = assignedBySku.get(p.sku) || 0, left = p.plan_qty - a;
                return (
                  <tr key={p.sku} className={left < 0 ? 'pa-over' : left === 0 ? 'pa-done' : ''} onClick={() => addRow({ mbsku: p.sku, qty_bags: String(Math.max(left, 0)) })} title={p.sku}>
                    <td>{p.product_no != null ? p.product_no : ''}</td>
                    <td className="pa-name" title={`${p.sku}${p.brand ? ' · ' + p.brand : ''}`}>{p.name}{p.brand && p.brand !== 'Organo Republic' && <span className="pp-muted"> · {p.brand}</span>}</td>
                    <td className="pa-num">{fmtN(p.plan_qty)}</td>
                    <td className="pa-num">{fmtN(a)}</td>
                    <td className="pa-num">{fmtN(left)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </aside>
      <section className="pa-rows">
        {!rows.length && <div className="pp-muted pa-pad">No rows yet — “Fill from plan”, “Copy previous day” or “+ Row”.</div>}
        {rows.length > 0 && (
          <table className="pa-table pa-rows-table">
            <thead><tr><th>#</th><th>Employee</th><th>Station / Activity</th><th>Product</th><th className="pa-num">Bags</th><th className="pa-num">Boxes</th><th>Comment</th><th /></tr></thead>
            <tbody>
              {rows.map((r, i) => {
                const isAct = r.target.startsWith('ac:');
                const p = productsBySku.get(r.mbsku);
                const type = p ? p.type : null;
                const stations = (ctx.stations || []).filter(s => (s.status === 'active' && (!type || s.product_types.includes(type))) || `st:${s.id}` === r.target);
                return (
                  <tr key={r.key}>
                    <td className="pp-muted">{i + 1}</td>
                    <td>
                      <select value={r.employee_id} onChange={e => patchRow(r.key, { employee_id: e.target.value })} className={!r.employee_id ? 'pa-missing' : ''}>
                        <option value="">— employee —</option>
                        {(ctx.employees || []).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                      </select>
                    </td>
                    <td>
                      <select value={r.target} onChange={e => {
                        const t = e.target.value;
                        patchRow(r.key, t.startsWith('ac:') ? { target: t, mbsku: '', qty_bags: '' } : { target: t });
                      }}>
                        <option value="">—</option>
                        <optgroup label="Stations">
                          {stations.map(s => {
                            const who = (usersOfStation.get(`st:${s.id}`) || []).filter(u => u.rowKey !== r.key).map(u => u.name);
                            return <option key={s.id} value={`st:${s.id}`}>{s.label}{who.length ? ` — busy: ${who.join(', ')}` : ''}{s.status !== 'active' ? ` (${s.status})` : ''}</option>;
                          })}
                        </optgroup>
                        <optgroup label="Activities">
                          {(ctx.activities || []).filter(a => a.is_active || `ac:${a.name}` === r.target).map(a => <option key={a.id} value={`ac:${a.name}`}>{a.name}</option>)}
                        </optgroup>
                      </select>
                    </td>
                    <td>
                      <select value={r.mbsku} disabled={isAct} onChange={e => {
                        const sku = e.target.value;
                        patchRow(r.key, { mbsku: sku, qty_bags: sku ? remainingFor(sku, r.key) : '' });
                      }}>
                        <option value="">{isAct ? '' : '— product —'}</option>
                        {planRows.length > 0 && <optgroup label="Plan for the day">
                          {planRows.map(x => <option key={x.sku} value={x.sku}>{x.product_no != null ? `${x.product_no} · ` : ''}{x.name}</option>)}
                        </optgroup>}
                        <optgroup label="Other products">
                          {others.map(x => <option key={x.sku} value={x.sku}>{x.product_no != null ? `${x.product_no} · ` : ''}{x.name}</option>)}
                        </optgroup>
                      </select>
                    </td>
                    <td className="pa-num"><input type="number" min="0" step="1" value={r.qty_bags} disabled={isAct} onChange={e => patchRow(r.key, { qty_bags: e.target.value })} className="pa-qty" /></td>
                    <td className="pa-num pp-muted">{isAct ? '' : boxesOf(r.qty_bags, p && p.bags_per_box)}{!isAct && p && p.box_size ? <span className="pa-box">{p.box_size}</span> : null}</td>
                    <td><input type="text" value={r.comment} onChange={e => patchRow(r.key, { comment: e.target.value })} placeholder="Comment" className="pa-comment" /></td>
                    <td><button className="pa-x" onClick={() => removeRow(r.key)} title="Remove row">×</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};

// ── Messages tab ─────────────────────────────────────────────────────────────
const MessagesTab = ({ messages, dirty, templates, onTemplates, say, setErr }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(templates);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (!editing) setDraft(templates); }, [templates, editing]);

  const copyOne = async m => say((await copyText(m.text)) ? `Copied — ${m.who}` : 'Copy failed');
  const copyAll = async () => say((await copyText(messages.map(m => m.text).join('\n\n— — —\n\n'))) ? `Copied ${messages.length} messages` : 'Copy failed');
  const saveTemplates = async () => {
    setSaving(true); setErr(null);
    try { const r = await api.put('/plantoproduce/message-templates', draft); onTemplates(r.data); setEditing(false); say('Templates saved'); }
    catch (e) { setErr(errMsg(e, 'Failed to save templates')); } finally { setSaving(false); }
  };

  return (
    <div className="pa-messages">
      <div className="pa-bar pa-bar-sub">
        <span className="pp-muted">{messages.length} message{messages.length === 1 ? '' : 's'}{dirty ? ' · from unsaved rows of the Day tab' : ''}</span>
        <span className="pa-grow" />
        <button className="pp-btn" onClick={() => setEditing(v => !v)}>{editing ? 'Hide templates' : 'Edit templates'}</button>
        <button className="pp-btn pp-btn-primary" onClick={copyAll} disabled={!messages.length}>Copy all</button>
      </div>
      {editing && (
        <div className="pa-templates">
          <div className="pp-muted">Placeholders: {PLACEHOLDERS.join(' ')}. A line whose placeholders are all empty is skipped. Empty template = default. Activity messages are edited in Settings → Activities.</div>
          <div className="pa-templates-grid">
            {PRODUCT_TYPES.map(t => (
              <label key={t}>{TYPE_LABELS[t]}<textarea rows={12} value={draft[t] || ''} onChange={e => setDraft(d => ({ ...d, [t]: e.target.value }))} /></label>
            ))}
          </div>
          <div className="pp-modal-actions"><button className="pp-btn pp-btn-primary" onClick={saveTemplates} disabled={saving}>{saving ? 'Saving…' : 'Save templates'}</button></div>
        </div>
      )}
      {!messages.length && <div className="pp-muted pa-pad">No messages: rows need an employee and a product (or an activity with a template).</div>}
      <div className="pa-msg-grid">
        {messages.map(m => (
          <div key={m.key} className="pa-msg">
            <div className="pa-msg-head"><div><b>{m.who}</b><div className="pp-muted pa-msg-sub">{m.sub}</div></div><button className="pp-btn pp-btn-sm" onClick={() => copyOne(m)}>Copy</button></div>
            <pre>{m.text}</pre>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── History tab ──────────────────────────────────────────────────────────────
const HistoryTab = ({ ctx, date, setErr }) => {
  const [f, setF] = useState({ from: addDays(date, -30), to: date, shift_id: '', employee_id: '', station_id: '', activity: '', q: '' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const params = {};
      for (const [k, v] of Object.entries(f)) if (v !== '') params[k] = v;
      const r = await api.get('/plantoproduce/assignments', { params });
      setData(r.data.rows || []);
    } catch (e) { setErr(errMsg(e, 'Failed to load history')); } finally { setLoading(false); }
  }, [f, setErr]);
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const summary = useMemo(() => {
    if (!data) return [];
    const absence = new Set((ctx.activities || []).filter(a => a.is_absence).map(a => a.name));
    const m = new Map();
    for (const r of data) {
      let s = m.get(r.employee_id);
      if (!s) m.set(r.employee_id, s = { name: r.employee || `#${r.employee_id}`, prod: new Set(), other: new Set(), off: new Set(), bags: 0 });
      if (r.mbsku) { s.prod.add(r.date); s.bags += Number(r.qty_bags) || 0; }
      else if (r.activity && absence.has(r.activity)) s.off.add(r.date);
      else if (r.activity) s.other.add(r.date);
    }
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [data, ctx]);

  const set = k => e => setF(x => ({ ...x, [k]: e.target.value }));
  return (
    <div className="pa-history">
      <div className="pa-bar pa-bar-sub pa-wrap">
        <label>From <input type="date" value={f.from} onChange={set('from')} /></label>
        <label>To <input type="date" value={f.to} onChange={set('to')} /></label>
        <label>Shift <select value={f.shift_id} onChange={set('shift_id')}><option value="">all</option>{(ctx.shifts || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></label>
        <label>Employee <select value={f.employee_id} onChange={set('employee_id')}><option value="">all</option>{(ctx.employees || []).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}</select></label>
        <label>Station <select value={f.station_id} onChange={set('station_id')}><option value="">all</option>{(ctx.stations || []).map(s => <option key={s.id} value={s.id}>{s.label}</option>)}</select></label>
        <label>Activity <select value={f.activity} onChange={set('activity')}><option value="">all</option>{(ctx.activities || []).map(a => <option key={a.id} value={a.name}>{a.name}</option>)}</select></label>
        <label>Search <input type="text" value={f.q} onChange={set('q')} placeholder="product, comment…" onKeyDown={e => { if (e.key === 'Enter') load(); }} /></label>
        <button className="pp-btn pp-btn-primary" onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Load'}</button>
      </div>
      {summary.length > 0 && (
        <table className="pa-table pa-summary">
          <thead><tr><th>Employee</th><th className="pa-num">Production days</th><th className="pa-num">Other days</th><th className="pa-num">Days off</th><th className="pa-num">Bags</th></tr></thead>
          <tbody>{summary.map(s => <tr key={s.name}><td>{s.name}</td><td className="pa-num">{s.prod.size}</td><td className="pa-num">{s.other.size}</td><td className="pa-num">{s.off.size}</td><td className="pa-num">{fmtN(s.bags)}</td></tr>)}</tbody>
        </table>
      )}
      {data && !data.length && <div className="pp-muted pa-pad">Nothing found</div>}
      {data && data.length > 0 && (
        <table className="pa-table">
          <thead><tr><th>Date</th><th>Shift</th><th>Employee</th><th>Station / Activity</th><th>Product</th><th className="pa-num">Bags</th><th>Comment</th><th>By</th></tr></thead>
          <tbody>
            {data.map(r => (
              <tr key={r.id}>
                <td>{fmtUS(r.date)}</td><td>{r.shift || ''}</td><td>{r.employee || ''}</td>
                <td>{r.station_label || r.activity || ''}</td>
                <td title={r.mbsku || ''}>{r.product || r.mbsku || ''}</td>
                <td className="pa-num">{r.qty_bags == null ? '' : fmtN(r.qty_bags)}</td>
                <td className="pa-wrap-cell">{r.comment || ''}</td>
                <td className="pp-muted" title={r.created_by || ''}>{r.created_by_name || r.created_by || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ── Settings tab: three dictionaries, one generic editor ─────────────────────
const SettingsTab = ({ onChanged, setErr, say }) => {
  const [dicts, setDicts] = useState(null);
  const reload = useCallback(async () => {
    const [st, sh, ac] = await Promise.all([api.get('/plantoproduce/stations'), api.get('/plantoproduce/shifts'), api.get('/plantoproduce/activities')]);
    setDicts({ stations: st.data, shifts: sh.data, activities: ac.data });
  }, []);
  useEffect(() => { reload().catch(e => setErr(errMsg(e, 'Failed to load settings'))); }, [reload, setErr]);
  if (!dicts) return <div className="pp-muted">Loading…</div>;

  const stationOpts = dicts.stations.filter(s => !s.merged_into);
  const specs = [
    { path: 'stations', title: 'Stations & machines', hint: 'Merged station disappears from the lists; the target shows as “2 - 3”. Maintenance / retired stations are hidden from the Day tab.',
      blank: { name: '', product_types: 'sets', status: 'active', merged_into: '', sort_order: 0 },
      columns: [
        { key: 'name', label: 'Name', type: 'text' },
        { key: 'product_types', label: 'Used for', type: 'types' },
        { key: 'status', label: 'Status', type: 'select', options: ['active', 'maintenance', 'retired'].map(v => [v, v]) },
        { key: 'merged_into', label: 'Merged into', type: 'select', options: [['', '—'], ...stationOpts.map(s => [String(s.id), s.name])], skipSelf: true },
        { key: 'sort_order', label: 'Order', type: 'number' },
      ] },
    { path: 'shifts', title: 'Shifts', blank: { name: '', sort_order: 0, is_active: 1 },
      columns: [{ key: 'name', label: 'Name', type: 'text' }, { key: 'sort_order', label: 'Order', type: 'number' }, { key: 'is_active', label: 'Active', type: 'check' }] },
    { path: 'activities', title: 'Activities (non-production)', hint: 'Message template: {Employee} and {Date} are available; empty = no message for this activity. “Absence” rows count as days off in History.',
      blank: { name: '', is_absence: 0, is_active: 1, sort_order: 0, message_template: '' },
      columns: [
        { key: 'name', label: 'Name', type: 'text' }, { key: 'is_absence', label: 'Absence', type: 'check' }, { key: 'is_active', label: 'Active', type: 'check' },
        { key: 'sort_order', label: 'Order', type: 'number' }, { key: 'message_template', label: 'Message template', type: 'textarea' },
      ] },
  ];
  return (
    <div className="pa-settings">
      {specs.map(sp => (
        <DictEditor key={sp.path} spec={sp} rows={dicts[sp.path]} onSaved={async () => { await reload(); onChanged(); }} setErr={setErr} say={say} />
      ))}
    </div>
  );
};

const Field = ({ col, value, onChange, rowId }) => {
  if (col.type === 'text') return <input type="text" value={value == null ? '' : value} onChange={e => onChange(e.target.value)} />;
  if (col.type === 'number') return <input type="number" value={value == null ? '' : value} onChange={e => onChange(e.target.value)} className="pa-qty" />;
  if (col.type === 'check') return <input type="checkbox" checked={!!Number(value)} onChange={e => onChange(e.target.checked ? 1 : 0)} />;
  if (col.type === 'textarea') return <textarea rows={3} value={value == null ? '' : value} onChange={e => onChange(e.target.value)} />;
  if (col.type === 'select') return (
    <select value={value == null ? '' : String(value)} onChange={e => onChange(e.target.value)}>
      {col.options.filter(([v]) => !(col.skipSelf && rowId != null && v === String(rowId))).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
  if (col.type === 'types') {
    const set = new Set(String(value || '').split(',').filter(Boolean));
    return (
      <span className="pa-types">
        {PRODUCT_TYPES.map(t => (
          <label key={t} className="pa-chk"><input type="checkbox" checked={set.has(t)} onChange={e => { const n = new Set(set); e.target.checked ? n.add(t) : n.delete(t); onChange(PRODUCT_TYPES.filter(x => n.has(x)).join(',')); }} />{TYPE_LABELS[t]}</label>
        ))}
      </span>
    );
  }
  return null;
};

const DictEditor = ({ spec, rows, onSaved, setErr, say }) => {
  const [drafts, setDrafts] = useState({});       // id -> partial changes
  const [adding, setAdding] = useState(spec.blank);
  const [busy, setBusy] = useState(false);
  const val = (r, k) => (drafts[r.id] && drafts[r.id][k] !== undefined ? drafts[r.id][k] : r[k]);
  const change = (id, k, v) => setDrafts(d => ({ ...d, [id]: { ...(d[id] || {}), [k]: v } }));
  const saveRow = async id => {
    setBusy(true); setErr(null);
    try { await api.put(`/plantoproduce/${spec.path}/${id}`, drafts[id]); setDrafts(d => { const n = { ...d }; delete n[id]; return n; }); await onSaved(); say('Saved'); }
    catch (e) { setErr(errMsg(e, 'Failed to save')); } finally { setBusy(false); }
  };
  const add = async () => {
    setBusy(true); setErr(null);
    try { await api.post(`/plantoproduce/${spec.path}`, adding); setAdding(spec.blank); await onSaved(); say('Added'); }
    catch (e) { setErr(errMsg(e, 'Failed to add')); } finally { setBusy(false); }
  };
  return (
    <div className="pa-dict">
      <div className="pa-sub">{spec.title}</div>
      {spec.hint && <div className="pp-muted pa-hint">{spec.hint}</div>}
      <table className="pa-table">
        <thead><tr>{spec.columns.map(c => <th key={c.key}>{c.label}</th>)}<th /></tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className={r.merged_into ? 'pa-merged' : ''}>
              {spec.columns.map(c => <td key={c.key}><Field col={c} value={val(r, c.key)} rowId={r.id} onChange={v => change(r.id, c.key, v)} /></td>)}
              <td>{drafts[r.id] && <button className="pp-btn pp-btn-sm pp-btn-primary" onClick={() => saveRow(r.id)} disabled={busy}>Save</button>}</td>
            </tr>
          ))}
          <tr className="pa-add">
            {spec.columns.map(c => <td key={c.key}><Field col={c} value={adding[c.key]} onChange={v => setAdding(a => ({ ...a, [c.key]: v }))} /></td>)}
            <td><button className="pp-btn pp-btn-sm" onClick={add} disabled={busy || !String(adding.name || '').trim()}>+ Add</button></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default AssignmentsModal;
